/**
 * File type utility functions
 */

import { logInfo, logDebug, logWarn, logHighlightOperation } from './debugLogger';

/**
 * Get the file extension from a file path or name
 */
export const getFileExtension = (filename: string | null): string => {
  if (!filename) return '';
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
};

/**
 * Check if a file is a PDF
 */
export const isPdfFile = (filename: string | null): boolean => {
  if (!filename) return false;
  return getFileExtension(filename) === 'pdf';
};

/**
 * Check if a file is a Word document
 */
export const isWordFile = (filename: string | null): boolean => {
  if (!filename) return false;
  const ext = getFileExtension(filename);
  return ext === 'doc' || ext === 'docx';
};

/**
 * Check if a file is an image
 */
export const isImageFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext);
};

/**
 * Check if a file can be converted to PDF
 */
export const isConvertibleToPdf = (filename: string): boolean => {
  return isWordFile(filename);
};

/**
 * Get the HTML preview URL for a document
 */
export const getDocumentPreviewUrl = (baseUrl: string, filename: string, quotes?: string[]): string => {
  if (isPdfFile(filename) || isWordFile(filename)) {
    let url = `${baseUrl}/${filename}/html`;

    // If quotes are provided, add them as query parameters for highlighting
    if (quotes && quotes.length > 0) {
      // Encode quotes for URL safety
      const encodedQuotes = quotes.map(q => encodeURIComponent(q)).join(',');
      url += `?quotes=${encodedQuotes}`;
    }

    return url;
  }
  return '';
};

/**
 * Get the direct file URL
 */
export const getDocumentUrl = (baseUrl: string, filename: string): string => {
  return `${baseUrl}/${filename}`;
};

/**
 * Escape HTML special characters to prevent injection
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Highlight text in HTML content with yellow background
 * Improved to handle HTML entities, whitespace variations, and partial matches
 */

export const highlightTextInHtml = (htmlContent: string, textToHighlight: string[]): string => {
  if (!textToHighlight || textToHighlight.length === 0 || !htmlContent) {
    logWarn("Highlighting skipped - missing content or quotes");
    return htmlContent;
  }

  logInfo(`Starting highlighting operation`, {
    contentLength: htmlContent.length,
    quotesCount: textToHighlight.length
  });

  let processedHtml = htmlContent;
  const successfulMatches: string[] = [];
  const successfulReplacements: number[] = [];

  // First pass: attempt exact matching with full quotes
  textToHighlight.forEach((text, index) => {
    if (!text || text.trim() === '') {
      logWarn(`Empty quote at index ${index}, skipping`);
      return;
    }

    // Clean and prepare the text for regex
    const cleanText = text.trim();
    const escapedText = escapeRegExp(cleanText);

    logDebug(`Processing quote ${index}`, {
      quoteLength: cleanText.length,
      quoteSample: cleanText.substring(0, 50) + (cleanText.length > 50 ? '...' : '')
    });

    // Create a replacement with highlighting
    const replacement = `<span class="highlighted-text" data-highlight-id="${index}" style="background-color: #FFFF00; display: inline;">${cleanText}</span>`;

    // Use regex to replace all occurrences while preserving case
    const regex = new RegExp(escapedText, 'g');

    // Check if there are any matches before replacing
    const hasMatches = regex.test(processedHtml);
    regex.lastIndex = 0; // Reset regex index

    if (hasMatches) {
      processedHtml = processedHtml.replace(regex, replacement);
      successfulMatches.push(cleanText);
      successfulReplacements.push(index);
      logDebug(`Successfully highlighted exact match for quote ${index}`);
    } else {
      logWarn(`No exact match found for quote ${index}`);
    }
  });

  // Second pass: try to match quotes that didn't match exactly using a more flexible approach
  textToHighlight.forEach((text, index) => {
    // Skip quotes that were already successfully matched
    if (successfulReplacements.includes(index) || !text || text.trim() === '') return;

    logDebug(`Attempting flexible matching for quote ${index}`);

    // Split the quote into sentences and try to match each one
    const cleanText = text.trim();

    // Split by sentence boundaries, minimum 15 chars to avoid tiny matches
    const sentences = cleanText.split(/(?<=[.!?])\s+/).filter(s => s.trim().length >= 15);
    logDebug(`Split quote ${index} into ${sentences.length} sentences`);

    if (sentences.length > 0) {
      sentences.forEach((sentence, sentIdx) => {
        const sentenceClean = sentence.trim();
        const escapedSentence = escapeRegExp(sentenceClean);

        // Allow for some variation in whitespace by making spaces optional
        const flexiblePattern = escapedSentence
          .replace(/\s+/g, '\\s*') // Replace spaces with \s* (zero or more whitespace chars)
          .replace(/([.!?,;:])/g, '\\s*$1\\s*'); // Allow optional spaces around punctuation

        const regex = new RegExp(flexiblePattern, 'gi');

        // Check if there are any matches
        const hasMatches = regex.test(processedHtml);
        regex.lastIndex = 0; // Reset regex index

        if (hasMatches) {
          const replacement = `<span class="highlighted-text" data-highlight-id="${index}-${sentIdx}" style="background-color: #FFFF00; display: inline;">$&</span>`;
          processedHtml = processedHtml.replace(regex, replacement);
          logDebug(`Successfully highlighted sentence ${sentIdx} from quote ${index}`);
        } else {
          logWarn(`No flexible match found for sentence ${sentIdx} of quote ${index}`);
        }
      });
    }
  });

  // Count successful highlights
  const highlightCount = (processedHtml.match(/background-color: #FFFF00/g) || []).length;

  // Log overall statistics
  logHighlightOperation(
    "document",
    htmlContent.length,
    textToHighlight,
    highlightCount
  );

  // Add a debug info comment at the end of the HTML
  const debugInfo = `
  <!--
  Document highlighting debug info:
  - Total quotes attempted: ${textToHighlight.length}
  - Successfully matched quotes: ${successfulMatches.length}
  - Quotes that matched exactly: ${successfulMatches.map((m, i) => `"${m.substring(0, 30)}..."`).join(', ')}
  - Total highlight spans: ${highlightCount}
  -->`;

  // Add the debug info before the closing body tag
  processedHtml = processedHtml.replace('</body>', `${debugInfo}\n</body>`);

  return processedHtml;
};